import { FirebaseError } from '@firebase/app';
import { AddressDocument, addressSchema } from '@models/address';
import {
  OrderDocument,
  OrderItemDocument,
  OrderItemInput,
  orderItemSchema,
  orderSchema,
} from '@models/order';
import { UserDocument } from '@models/user';
import { BreadcrumbItem } from '@ui/Breadcrumb';
import {
  CreatePaymentArgs,
  MinimalOrderDocument,
  PaymentMetadata,
  UpdatePaymentArgs,
} from '@util/firestore/payments/payments.types';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { CategoryDocument } from 'models/category';
import { ProductDocument, Variation } from 'models/product';
import { Bike, RideType, Vehicle } from 'models/shared';
import sanitize from 'sanitize-html';
import { getDiscount } from './firestore/cart';
import { getOrderId, getOrderNum } from './firestore/orders';
import {
  getProductById,
  getSizeFromVariation,
  getVariationPrice,
} from './firestore/products';
import { logError } from './logError';
import { Cart, CartItem, cartItemSchema } from './types/firestore/carts';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { SearchResponseFacetCountSchema } from 'typesense/lib/Typesense/Documents';
import { getNumber } from './createListingHelpers';
import { getTax } from './firestore/taxjar';
import weekday from 'dayjs/plugin/weekday';
import { OfferDocument } from 'models/offer';
dayjs.extend(weekday);
export const IMAGE_PATH = process.env.NEXT_PUBLIC_IS_STAGING
  ? 'https://mxlocker-staging.b-cdn.net/'
  : 'https://cdn.mxlocker.com/';
export const IMAGE_SIZE = '1920x2560';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getCartItemFromProductDocument(
  product: ProductDocument,
  selectedVariations?: Variation[] | null,
  attribution?: OrderItemDocument['attribution'],
  customInputs?: {
    enabled: boolean;
    values: Record<string, string | number>;
    cost: number;
  },
  qty: number = 1
): CartItem {
  const basePrice = product.price;
  const customInputCost = customInputs?.enabled ? customInputs.cost : 0;

  const item = {
    product_id: product.id,
    product_title: product.title,
    product_image: product.thumbnail,
    product_link: product.slug,
    seller_id: product.seller_id,
    account_id: product.account_id,
    product_cost: selectedVariations?.length
      ? getVariationPrice(product, selectedVariations)
      : basePrice + customInputCost,
    qty,
    is_flat_rate: product.is_flat_rate,
    ...(selectedVariations && {
      size: getSizeFromVariation(selectedVariations),
    }),
    ...(selectedVariations?.find((v) => v.color) && {
      color: selectedVariations.find((v) => v.color)!.color as string,
    }),
    ...(customInputs && { custom_inputs: customInputs }),
    attribution: attribution ?? {},
  };
  const res = cartItemSchema.safeParse(item);
  if (!res.success) {
    logError(res.error, 'getCartItemFromProductDocument');
    return item;
  }
  return res.data;
}

export const cartItemToProductDocument = async (
  item: CartItem
): Promise<ProductDocument | null> => {
  return getProductById(item.product_id);
};

export const cartItemToOrderItem = (item: CartItem): OrderItemDocument => {
  const hasVariations = item.size?.letter || item.size?.number || item.color;
  const orderItem: OrderItemInput = {
    id: item.product_id,
    seller_id: item.seller_id,
    qty: item.qty,
    state: 0,
    product_cost: item.product_cost,
    product_name: item.product_title,
    product_tax: item.tax ?? 0,
    buyer_shipping_cost: item.shipping_cost ?? 0,
    account_id: item.account_id,
    rate_id: item.rate_id,
    ...(hasVariations && {
      variations: {
        ...(item.size?.letter && { letter: item.size.letter }),
        ...(!!item.size?.number && { number: item.size.number }),
        ...(item.color && { color: item.color }),
      },
    }),
    attribution: item.attribution ?? {},
    // custom_inputs: item.custom_inputs?.values ?? {},
  };
  if (item.custom_inputs?.enabled) {
    orderItem.custom_inputs = item.custom_inputs.values;
  }
  const res = orderItemSchema.safeParse(orderItem);
  if (!res.success) {
    logError(res.error, 'cartItemToOrderItem', item.product_id);
    return orderItem as OrderItemDocument;
  }
  return res.data;
};

export function formatAddress(address: AddressDocument): string {
  return `${address.address_line1}, ${address.city_locality} ${address.state_province} ${address.postal_code}, ${address.country_code}`;
}

const metaData = (order: MinimalOrderDocument, is_guest: boolean) => {
  const items = Object.keys(order.sellers!).reduce((acc, key) => {
    order.sellers![key].forEach((item) => {
      (item as any).seller_id = key;
      const itemCopy = { ...item };
      delete itemCopy.product_name;
      acc[item.id] = JSON.stringify(itemCopy);
      return acc;
    });
    return acc;
  }, {} as { [seller_id: string]: string });
  return {
    ...items,
    address: JSON.stringify(order.address),
    order_id: order.id ?? '',
    buyer_id: order.buyer_id,
    from_web: 'true',
    is_guest: is_guest ? 'true' : 'false',
    ...(order.donate && { donation: 'R2R' }),
    ...(order.coupon?.id && { promo: order.coupon?.id }),
    ...(order.affirm_fee && { affirm_fee: order.affirm_fee.toFixed(2) }),
    ...(order.ra_fee && { ra_fee: order.ra_fee.toFixed(2) }),
  };
};

export async function getCreatePaymentIntentArgs({
  cart,
  userDoc,
  payment,
  selectedPaymentMethod,
}: {
  cart: Cart;
  userDoc: UserDocument;
  payment: OrderDocument['payment'];
  selectedPaymentMethod?: string;
}): Promise<{
  amount: number;
  order: OrderDocument;
  metadata: PaymentMetadata;
  currency: string;
}> {
  const id = getOrderId();
  if (!id) throw new Error('Could not get order id');
  if (!cart.shipping_address) throw new Error('No shipping address selected');
  const itemsTotal = cart.items.reduce((acc, item) => {
    return acc + item.product_cost * item.qty;
  }, 0);
  const discountTotal = getDiscount(itemsTotal, cart.coupons, cart.items);

  const c = cart.coupons?.[0]; // just using 1 coupon for MVP
  const coupon: OrderDocument['coupon'] = c?.id
    ? {
        id: c.id,
        code: c.code,
        off: discountTotal,
        ...(c?.seller_id && { seller_id: c.seller_id }),
        ...(c?.account_id && { account_id: c.account_id }),
      }
    : undefined;

  const sellers = cart.items.reduce<{ [key: string]: OrderItemDocument[] }>(
    (acc, item) => {
      const orderItem = cartItemToOrderItem(item);
      orderItem.product_tax = item.tax ?? 0;
      if (acc[item.seller_id]) {
        acc[item.seller_id].push(orderItem);
      } else {
        acc[item.seller_id] = [orderItem];
      }
      return acc;
    },
    {}
  );

  const item_count = cart.items.reduce((acc, item) => acc + item.qty, 0);

  const subtotal = itemsTotal - discountTotal;

  const shippingTotal = cart.items.reduce((acc, item) => {
    return acc + (item.shipping_cost ?? 0);
  }, 0);

  const total_after_tax =
    subtotal +
    shippingTotal +
    cart.tax +
    (cart.donation_amount ?? 0) +
    (cart.ra_fee ?? 0);
  const affirm_fee =
    selectedPaymentMethod === 'affirm' ? 0.03 * total_after_tax : 0;
  const total = total_after_tax + affirm_fee;
  // get temp order_num so zod parse doesn't fail (backend still hasn't finished)
  const order_num = await getOrderNum();
  const order: MinimalOrderDocument = {
    affirm_fee,
    address: cart.shipping_address,
    buyer_id: userDoc.uid,
    coupon,
    customer_id: userDoc.customer_id ?? '',
    donate: !!(cart.donation_amount ?? 0),
    id,
    order_num,
    item_count,
    payment: {
      type: selectedPaymentMethod === 'affirm' ? 6 : payment.type,
      saved: !!payment.id && payment.type === 0,
      ...(payment.id && payment.type === 0 && { id: payment.id }),
    },
    product_ids: cart.items.map((item) => item.product_id),
    sellers,
    shipping_total: shippingTotal,
    subtotal,
    total,
    tax: cart.tax ?? 0,
    ra_fee: cart.ra_fee ?? 0,
  };
  const orderParsed = orderSchema.parse(order);

  return {
    amount: total,
    order: orderParsed,
    currency: 'usd',
    metadata: metaData(order, !!userDoc.is_guest),
  };
}

const nfCache = new Map<string, Intl.NumberFormat>();

function fallbackCurrency(n: number, code: string) {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const [int, frac] = abs.toFixed(2).split('.');
  const withGroups = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${withGroups}.${frac} ${code}`;
}

export function formatCurrency(
  amount: number,
  currency = 'USD',
  locale?: string
) {
  const code = currency.toUpperCase();

  // Fix a small bug in your original: CAD branch was formatting as USD.
  // If you need a special CAD display, handle it *after* formatting in CAD.
  try {
    if (!globalThis.Intl || !Intl.NumberFormat) throw new Error('no-intl');
    const key = `${locale ?? ''}:${code}`;
    let nf = nfCache.get(key);
    if (!nf) {
      nf = new Intl.NumberFormat(locale, { style: 'currency', currency: code });
      nfCache.set(key, nf);
    }
    const formatted = nf.format(amount);
    // Example of a custom CAD suffix (optional)
    // if (code === 'CAD') return `${formatted} CAD`;
    return formatted;
  } catch {
    return fallbackCurrency(amount, code);
  }
}

export function normalizePrice(price: number) {
  if (price % 1 === 0) return price.toString();
  return price
    .toFixed(2)
    .replace(/\.0+$/, '')
    .replace(/(\.[0-9]*?)0+$/, '$1');
}

export function formatTimestamp(timestamp?: number, hideTime = false) {
  if (!timestamp) return '';
  if (timestamp.toString().length === 10) timestamp *= 1000;
  return new Date(timestamp).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: hideTime ? undefined : 'numeric',
    minute: hideTime ? undefined : 'numeric',
    hour12: hideTime ? true : undefined,
  });
}
export function formatTime(timestamp: number) {
  const date = new Date(timestamp);
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatAuthError(error: FirebaseError) {
  if (error.code === 'auth/invalid-browser') {
    return 'Invalid Browser. Please open in this page in your default browser.';
  }
  const words = error.code.replace('auth/', '').replaceAll('-', ' ').split(' ');
  const capitalizedWords = words.map((word) => {
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
  return capitalizedWords.join(' ');
}

export function formatMessage(msg: string) {
  if (msg && typeof msg === 'string') {
    const regex =
      /\b(https?):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|]/gi;
    const matches = msg.match(regex);
    if (matches?.length) {
      matches.forEach((match) => {
        msg = msg.replace(
          match,
          `<a href="${match}" target="_blank">${match}</a>`
        );
      });
      return { html: sanitize(msg) };
    }
  }
  return { html: msg };
}

export function nonNullable<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

export function getHostUrl() {
  if (process.env.NODE_ENV === 'production') {
    const host =
      typeof window !== 'undefined'
        ? window.location.host
        : process.env.NEXT_PUBLIC_IS_STAGING
        ? 'mxlocker.dev'
        : 'mxlocker.com';
    return `https://${host}`;
  }
  if (process.env.NODE_ENV === 'development') return 'http://localhost:8100';
  throw new Error('host not configured for current environment');
}

export function getErrorMessage(e: any): string {
  return (
    e.message?.message?.message || e.message?.message || e.message || e || ''
  );
}
export const CURRENCY_MAP = {
  usd: 'US Dollar',
  cad: 'Canadian Dollar',
  eur: 'Euro',
  aud: 'Australian Dollar',
  hkd: 'Hong Kong Dollar',
  mxn: 'Mexican Peso',
  nzd: 'New Zealand Dollar',
  nok: 'Norwegian Krone',
  sgd: 'Singapore Dollar',
  chf: 'Swiss Franc',
  thb: 'Thai Baht',
  gbp: 'British Pound',
} as const;

export function getSellerRegistrationCurrencyOptions(): {
  label: string;
  value: string;
  id: string;
}[] {
  const allCurrency = getSellerRegistrationCountries().map((c) => c.currency);
  return [...new Set(allCurrency)].sort().map((c) => ({
    label: CURRENCY_MAP[c],
    value: c,
    id: c,
  }));
}

export function getSellerRegistrationCountries() {
  const countries = [
    {
      code: 'US',
      name: 'United States',
      currency: 'usd',
    },
    {
      code: 'CA',
      name: 'Canada',
      currency: 'cad',
    },
    { code: 'AT', name: 'Austria', currency: 'eur' },
    {
      code: 'AU',
      name: 'Australia',
      currency: 'aud',
    },
    { code: 'BE', name: 'Belgium', currency: 'eur' },
    { code: 'BG', name: 'Bulgaria', currency: 'eur' },
    { code: 'HR', name: 'Croatia', currency: 'eur' },
    { code: 'CY', name: 'Cyprus', currency: 'eur' },
    {
      code: 'CZ',
      name: 'Czech Republic',
      currency: 'eur',
    },
    { code: 'DK', name: 'Denmark', currency: 'eur' },
    { code: 'EE', name: 'Estonia', currency: 'eur' },
    { code: 'FI', name: 'Finland', currency: 'eur' },
    { code: 'FR', name: 'France', currency: 'eur' },
    { code: 'DE', name: 'Germany', currency: 'eur' },
    { code: 'GR', name: 'Greece', currency: 'eur' },
    {
      code: 'HK',
      name: 'Hong Kong',
      currency: 'hkd',
    },
    { code: 'HU', name: 'Hungary', currency: 'eur' },
    { code: 'IE', name: 'Ireland', currency: 'eur' },
    { code: 'IT', name: 'Italy', currency: 'eur' },
    { code: 'LV', name: 'Latvia', currency: 'eur' },
    { code: 'LT', name: 'Lithuania', currency: 'eur' },
    { code: 'LU', name: 'Luxembourg', currency: 'eur' },
    { code: 'MT', name: 'Malta', currency: 'eur' },
    {
      code: 'MX',
      name: 'Mexico',
      currency: 'mxn',
    },
    { code: 'NL', name: 'Netherlands', currency: 'eur' },
    {
      code: 'NZ',
      name: 'New Zealand',
      currency: 'nzd',
    },
    {
      code: 'NO',
      name: 'Norway',
      currency: 'nok',
    },
    { code: 'PL', name: 'Poland ', currency: 'eur' },
    { code: 'PT', name: 'Portugal', currency: 'eur' },
    { code: 'RO', name: 'Romania', currency: 'eur' },
    {
      code: 'SG',
      name: 'Singapore',
      currency: 'sgd',
    },
    { code: 'SK', name: 'Slovakia', currency: 'eur' },
    { code: 'SI', name: 'Slovenia', currency: 'eur' },
    { code: 'ES', name: 'Spain', currency: 'eur' },
    { code: 'SE', name: 'Sweden', currency: 'eur' },
    {
      code: 'CH',
      name: 'Switzerland',
      currency: 'chf',
    },
    {
      code: 'TH',
      name: 'Thailand',
      currency: 'thb',
    },
    {
      code: 'GB',
      name: 'United Kingdom',
      currency: 'gbp',
    },
  ] as const;
  return countries;
}

export const offerStateToText = (state: number) => {
  switch (state) {
    case 1:
      return 'Accepted';
    case 2:
      return 'Declined';
    case 3:
      return 'Expired';
    default:
      return 'Pending';
  }
};

export const getOfferStatusBadge = (offer: OfferDocument) => {
  switch (offer.state) {
    case 1:
      return `Accepted - ${formatCurrency(offer.price)}`;
    case 2:
      return `Declined - ${formatCurrency(offer.price)}`;
    case 3:
      return `Expired - ${formatCurrency(offer.price)}`;
    default:
      return `Pending - ${formatCurrency(offer.price)}`;
  }
};

const rtfCache = new Map<string, Intl.RelativeTimeFormat>();

function getRtf(locale: string) {
  const key = locale;
  let rtf = rtfCache.get(key);
  if (!rtf) {
    rtf = new Intl.RelativeTimeFormat(locale, {
      style: 'narrow',
      numeric: 'auto',
    });
    rtfCache.set(key, rtf);
  }
  return rtf;
}

function fallbackTimeAgo(seconds: number, locale = 'en') {
  // Very small human fallback: "< 5s", "3m ago", "in 2h"
  const abs = Math.abs(seconds);
  const tbl: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
    ['second', 1],
  ];
  for (const [unit, unitSec] of tbl) {
    if (abs >= unitSec) {
      const value = Math.round(seconds / unitSec);
      // crude, english-only fallback; keep it super simple
      return value < 0
        ? `${Math.abs(value)} ${unit}${Math.abs(value) !== 1 ? 's' : ''} ago`
        : `in ${value} ${unit}${value !== 1 ? 's' : ''}`;
    }
  }
  return 'just now';
}

export function timeAgo(
  timestamp?: number,
  locale = 'en',
  now = Date.now()
): string {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const seconds = (date.getTime() - now) / 1000; // negative => past, positive => future

  try {
    if (!globalThis.Intl || typeof Intl.RelativeTimeFormat !== 'function') {
      // No Intl or no RTF support -> fallback
      return fallbackTimeAgo(seconds, locale);
    }
    const tbl: [Intl.RelativeTimeFormatUnit, number][] = [
      ['year', 60 * 60 * 24 * 365],
      ['month', 60 * 60 * 24 * 30],
      ['week', 60 * 60 * 24 * 7],
      ['day', 60 * 60 * 24],
      ['hour', 60 * 60],
      ['minute', 60],
      ['second', 1],
    ];
    const rtf = getRtf(locale);
    for (const [unit, unitSec] of tbl) {
      if (Math.abs(seconds) >= unitSec) {
        const value = Math.round(seconds / unitSec); // negative for past
        return rtf.format(value, unit);
      }
    }
    return 'just now';
  } catch {
    // Any unexpected runtime issues -> safe fallback
    return fallbackTimeAgo(seconds, locale);
  }
}

export const businessDaysAgo = (timestamp?: number) => {
  if (!timestamp) {
    timestamp = Date.now();
  }

  let targetDate = dayjs(timestamp);
  targetDate.set('hours', 0);
  targetDate.set('minutes', 0);
  targetDate.set('seconds', 0);
  let now = dayjs();
  now.set('hours', 0);
  now.set('minutes', 0);
  now.set('seconds', 0);
  let businessDaysAgo = 0;

  while (targetDate < now) {
    if (targetDate.day() !== 0 && targetDate.day() !== 6) {
      businessDaysAgo++;
    }
    targetDate = targetDate.add(1, 'day');
  }

  return businessDaysAgo;
};

// Dayjs version
export function businessDaysDiff(startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) {
  let count = 0;
  let current = startDate.startOf('day');

  while (current.isBefore(endDate, 'day')) {
    const day = current.day(); // 0 = Sun, 6 = Sat
    if (day !== 0 && day !== 6) {
      count++;
    }
    current = current.add(1, 'day');
  }

  return count;
}

export function timestampAfterBusinessDays(
  startTimestamp: number,
  businessDays: number
) {
  const start = dayjs(startTimestamp);

  // Function to add business days
  function addBusinessDays(startDate: Dayjs, numberOfDays: number) {
    let daysAdded = 0;
    while (daysAdded < numberOfDays) {
      startDate = startDate.add(1, 'day');
      // Monday = 1, Tuesday = 2, ..., Friday = 5
      if (startDate.weekday() >= 1 && startDate.weekday() <= 5) {
        daysAdded++;
      }
    }
    return startDate;
  }

  // Adding the specified number of business days
  const end = addBusinessDays(start, businessDays);

  // Return the end timestamp as a Date object
  return end.toDate().getTime();
}
// Hours minutes and seconds until timestamp is reached in the format #H #M #S
export const timeRemaining = (timestamp?: number) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const secondsRemaining = (date.getTime() - Date.now()) / 1000;

  const { days, hours, minutes, seconds } =
    getTimeRemainingParts(secondsRemaining);

  const timeRemaining = `${
    days ? `${days}d ` : ''
  }${hours}h ${minutes}m ${seconds}s`;
  return timeRemaining;
};

export const getTimeRemainingParts = (secondsRemaining: number) => {
  const days = Math.floor(secondsRemaining / 86400);
  const hours = Math.floor((secondsRemaining % 86400) / 3600);
  const minutes = Math.floor((secondsRemaining % 3600) / 60);
  const seconds = Math.floor(secondsRemaining % 60);
  return { days, hours, minutes, seconds };
};

export function isMobile() {
  return typeof window !== 'undefined' && window.innerWidth <= 768;
}

export function isTablet() {
  return typeof window !== 'undefined' && window.innerWidth <= 1024;
}

export function isDesktop() {
  return typeof window !== 'undefined' && window.innerWidth > 1024;
}

export function isLargeDesktop() {
  return typeof window !== 'undefined' && window.innerWidth > 1280;
}

export const debounce = (func: any, delay: number) => {
  let timerId: any;
  return function (this: any, ...args: any[]) {
    clearTimeout(timerId);
    timerId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

export const capitalize = (word?: string) => {
  if (!word) return '';
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const sentenceCase = (word?: string) => {
  if (!word) return '';
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

// title case, could be multiple words
export const titleCase = (words?: string) => {
  if (!words) return '';
  return words
    .split(' ')
    .map((word) => sentenceCase(word))
    .join(' ');
};

export const diffInDaysFromNow = (date: Date) => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  return diff / (1000 * 3600 * 24);
};
export const timestampToDatetimelocal = (timestamp: number) => {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  return `${year}-${
    month < 10 ? `0${month}` : month
  }-${day}T${hours}:${minutes}:${seconds}`;
};

export const datetimeLocalToTimestamp = (datetimeLocal: string) => {
  const date = new Date(datetimeLocal);
  return date.getTime();
};

export const getDurationInDays = (start: number, end: number): number => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diff = endDate.getTime() - startDate.getTime();
  return Math.floor(diff / (1000 * 3600 * 24));
};

export const timestampFromDuration = (duration: number, start: string) => {
  const now = new Date(start);
  const timestamp = now.getTime() + duration * 1000 * 3600 * 24;
  return timestamp;
};

export const getEndDate = (duration: number, start: string) => {
  const date = new Date(timestampFromDuration(duration, start));
  // return date and time
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
};

export const getDatePartsFromTimestamp = (timestamp?: number) => {
  if (!timestamp) return;
  const date = new Date(timestamp);
  const hour = date.getHours();
  const minute = date.getMinutes();

  const hourIn12 = hour % 12;

  return {
    date: date.toISOString().split('T')[0],
    hour: hourIn12 < 10 ? `0${hourIn12}` : `${hourIn12}`,
    minute: minute < 10 ? `0${minute}` : `${minute}`,
    AMPM: hour < 12 ? 'AM' : 'PM',
  };
};

export const inBetween = (startTime: string, duration: string) => {
  const now = new Date();
  const start = new Date(startTime);
  const end = new Date(timestampFromDuration(Number(duration), startTime));
  return now.getTime() > start.getTime() && now.getTime() < end.getTime();
};

export const getCDNUrl = (src: string) => {
  if (!src) return '';
  const baseURL =
    'https://firebasestorage.googleapis.com/v0/b/mxlocker-b1d4a.appspot.com/o/';
  if (src.includes(baseURL)) {
    src = src.replace(baseURL, 'https://cdn.mxlocker.com/');
    if (src.includes('image') && src.includes('thumb')) {
      const created: any = src.split('image')[1].slice(0, 13);
      const isNewFormat = !isNaN(created) && Number(created) >= 1663699424310;
      return isNewFormat && src.includes('?alt=media') && !src.includes('.webp')
        ? src.replace('?alt=media', '.webp?alt=media')
        : src;
    }
  }
  return src;
};

export const formatBike = (bike: Bike) => {
  return `${bike.year} ${bike.make} ${bike.model}`;
};

export const isEU = () => {
  dayjs.extend(timezone);
  const tz = dayjs.tz.guess();
  switch (tz) {
    case 'Europe/Vienna':
      return true;
    case 'Europe/Brussels':
      return true;
    case 'Europe/Sofia':
      return true;
    case 'Europe/Zagreb':
      return true;
    case 'Asia/Famagusta':
      return true;
    case 'Asia/Nicosia':
      return true;
    case 'Europe/Prague':
      return true;
    case 'Europe/Copenhagen':
      return true;
    case 'Europe/Tallinn':
      return true;
    case 'Europe/Helsinki':
      return true;
    case 'Europe/Paris':
      return true;
    case 'Europe/Berlin':
      return true;
    case 'Europe/Busingen':
      return true;
    case 'Europe/Athens':
      return true;
    case 'Europe/Budapest':
      return true;
    case 'Europe/Dublin':
      return true;
    case 'Europe/Rome':
      return true;
    case 'Europe/Riga':
      return true;
    case 'Europe/Vilnius':
      return true;
    case 'Europe/Luxembourg':
      return true;
    case 'Europe/Malta':
      return true;
    case 'Europe/Amsterdam':
      return true;
    case 'Europe/Warsaw':
      return true;
    case 'Atlantic/Azores':
      return true;
    case 'Atlantic/Madeira':
      return true;
    case 'Europe/Lisbon':
      return true;
    case 'Europe/Bucharest':
      return true;
    case 'Europe/Bratislava':
      return true;
    case 'Europe/Ljubljana':
      return true;
    case 'Africa/Ceuta':
      return true;
    case 'Atlantic/Canary':
      return true;
    case 'Europe/Madrid':
      return true;
    case 'Europe/Stockholm':
      return true;
    default:
      return false;
  }
};

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result + '';
      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export function capitalizeFirstLetter(string?: string) {
  if (!string) return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function formatRange(deliveryDates?: {
  earliest: string;
  latest: string;
}): string | null {
  if (!deliveryDates) return null;
  const { earliest, latest } = deliveryDates;
  const start = dayjs(earliest);
  const end = dayjs(latest);
  if (start.month() === end.month())
    return `${start.format('MMM DD')}-${end.format('DD')}`;
  return `${start.format('MMM DD')}-${end.format('MMM DD')}`;
}

export function isBot(userAgent: string) {
  return /Googlebot|Mediapartners-Google|AdsBot-Google|googleweblight|Storebot-Google|Google-PageRenderer|Google-InspectionTool|Bingbot|BingPreview|Slurp|DuckDuckBot|baiduspider|yandex|sogou|LinkedInBot|bitlybot|tumblr|vkShare|quora link preview|facebookexternalhit|facebookcatalog|Twitterbot|applebot|redditbot|Slackbot|Discordbot|WhatsApp|SkypeUriPreview|ia_archiver|curl/i.test(
    userAgent
  );
}

export function keysDiff(
  obj1: { [key: string]: any },
  obj2: { [key: string]: any },
  parentKey = '',
  ignoreNotCommon: boolean = false
): string[] {
  const keysWithDifferences: string[] = [];
  for (const key in obj1) {
    if (Object.prototype.hasOwnProperty.call(obj1, key)) {
      if (ignoreNotCommon && !Object.prototype.hasOwnProperty.call(obj2, key)) {
        continue;
      }

      const currentKey = parentKey ? `${parentKey}.${key}` : key;

      if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object') {
        keysWithDifferences.push(
          ...keysDiff(obj1[key], obj2[key], currentKey, ignoreNotCommon)
        );
      } else if (
        !Object.prototype.hasOwnProperty.call(obj2, key) ||
        obj1[key] !== obj2[key]
      ) {
        keysWithDifferences.push(currentKey);
      }
    }
  }
  for (const key in obj2) {
    if (
      Object.prototype.hasOwnProperty.call(obj2, key) &&
      !Object.prototype.hasOwnProperty.call(obj1, key)
    ) {
      if (ignoreNotCommon) {
        continue;
      }
      const currentKey = parentKey ? `${parentKey}.${key}` : key;
      keysWithDifferences.push(currentKey);
    }
  }
  return keysWithDifferences;
}

export function getBreadcrumbItems(
  category: CategoryDocument
): BreadcrumbItem[] {
  return (
    category.breadcrumbs?.map((bc) => ({
      label: bc.name,
      href: bc.slug,
      current: bc.name === category.name,
    })) ?? []
  );
}

export function getCountryLabelFromCode(code: string) {
  switch (code) {
    case 'US':
      return 'United States';
    case 'CA':
      return 'Canada';
    case 'MX':
      return 'Mexico';
    case 'PR':
      return 'Puerto Rico';
    case 'EU':
      return 'Europe';
    case 'AU':
      return 'Australia';
    case 'SAM':
      return 'South America';
    default:
      return code;
  }
}

export function rockyTypeToMXType(rockyType: Vehicle['type']) {
  const rockyTypeToMX: Record<Exclude<Vehicle['type'], 'WATER'>, RideType> = {
    DIRT_BIKE: 'Dirt Bikes',
    ATV: 'ATV',
    UTV: 'UTV',
    STREET: 'Street',
    TRIALS: 'Dirt Bikes',
    DUAL_SPORT: 'Street',
    SNOW: 'Snow',
  };

  if (rockyType === 'WATER') return undefined;

  return rockyTypeToMX[rockyType];
}

export function getMaxFacetCount(
  facet_counts: SearchResponseFacetCountSchema<ProductDocument>[],
  field?: string
): string {
  const flattenedCounts = facet_counts
    .filter((fc) => !field || fc.field_name === field)
    .flatMap((f) => f.counts.flat());
  const highestCount = flattenedCounts.reduce(
    (acc, curr) => {
      if (curr.count > acc.count) return curr;
      return acc;
    },
    { count: 0, value: '' }
  );
  return highestCount.value;
}

export const calcTax = async ({
  form,
  products,
}: {
  form: {
    price: number;
    shippingCost: number;
    shippingAddress: string;
  };
  products: ProductDocument[];
}): Promise<number> => {
  const total = getNumber(form.price);
  if (!form.shippingAddress) throw new Error('No shipping address selected');
  const shippingAddressObj = JSON.parse(form.shippingAddress);
  shippingAddressObj.address_line2 = shippingAddressObj.address_line2 ?? '';
  const shippingAddress = addressSchema.parse(shippingAddressObj);

  const promises = products.map(async (product) => {
    const tax = await getTax({
      product_id: product.id,
      seller_id: product.seller_id,
      to_address: shippingAddress!,
      total,
      shipping: form.shippingCost,
    });
    return tax.amount;
  });
  const res = await Promise.all(promises);
  return res.reduce((acc, curr) => (acc += curr), 0);
};

export async function revalidatePath(path: string, type?: 'page' | 'layout') {
  try {
    await fetch(`/api/revalidate?path=${path}&type=${type}`);
    return true;
  } catch (e) {
    logError(e, 'revalidate');
    return false;
  }
}

export const normalize = (input: string) => {
  // remove all non-alphanumeric characters
  return input.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
};

export const getSearchProductsUrl = () => {
  const region = process.env.NEXT_PUBLIC_DEPLOY_REGION as
    | 'us-east4'
    | 'us-west2'
    | 'us-central1';
  switch (region) {
    case 'us-east4':
      return `https://us-east4-${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}.cloudfunctions.net/v2_searchProductsEast`;
    case 'us-west2':
      return `https://us-west2-${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}.cloudfunctions.net/v2_searchProductsWest`;
    default:
      return `https://us-central1-${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}.cloudfunctions.net/v2_searchProducts`;
  }
};

export const findVariation = (
  product: ProductDocument,
  variation: {
    letter?: string;
    number?: number;
    color?: string;
  }
): Variation | undefined => {
  if (!product?.variations?.length) return;
  if (!variation) return;
  const foundVariation = product.variations?.find(
    (v) =>
      (!variation.color || v.color === variation.color) &&
      (!variation.letter || v.size === variation.letter) &&
      (!variation.number || v.size === variation.number)
  );
  return foundVariation;
};
